// src/utils/snapshotHelpers.ts
import type { SnapshotEstadoRequisito } from '@/services/snapshot.service';

export type CalificacionRequisito = 'C' | 'NC' | 'CP' | 'NA';

export const CALIFICACION_LABELS: Record<CalificacionRequisito, string> = {
  C: 'Cumple',
  NC: 'No Cumple',
  CP: 'Cumple Parcialmente',
  NA: 'No Aplica',
};

export const CALIFICACION_COLORS: Record<CalificacionRequisito, string> = {
  C: 'text-green-600',
  NC: 'text-red-600',
  CP: 'text-yellow-600',
  NA: 'text-gray-400',
};

/**
 * Obtiene el estado actual de un requisito
 */
export function getEstadoActual(estadosRequisitos: SnapshotEstadoRequisito, requisitoId: string): CalificacionRequisito | null {
  const estado = estadosRequisitos[requisitoId];
  return estado ? estado[0] : null;
}

/**
 * Obtiene el estado de asesoría de un requisito
 */
export function getEstadoAsesoria(estadosRequisitos: SnapshotEstadoRequisito, requisitoId: string): CalificacionRequisito | null {
  const estado = estadosRequisitos[requisitoId];
  return estado ? estado[1] : null;
}

/**
 * Compara dos snapshots y retorna los requisitos que cambiaron
 */
export function compararSnapshots(
  snapshotAnterior: SnapshotEstadoRequisito,
  snapshotActual: SnapshotEstadoRequisito
): {
  requisitoId: string;
  estadoAnterior: CalificacionRequisito | null;
  estadoActual: CalificacionRequisito | null;
}[] {
  const cambios: {
    requisitoId: string;
    estadoAnterior: CalificacionRequisito | null;
    estadoActual: CalificacionRequisito | null;
  }[] = [];

  const todosRequisitos = new Set([...Object.keys(snapshotAnterior), ...Object.keys(snapshotActual)]);

  for (const requisitoId of todosRequisitos) {
    const anterior = getEstadoActual(snapshotAnterior, requisitoId);
    const actual = getEstadoActual(snapshotActual, requisitoId);

    if (anterior !== actual) {
      cambios.push({
        requisitoId,
        estadoAnterior: anterior,
        estadoActual: actual,
      });
    }
  }

  return cambios;
}

/**
 * Calcula estadísticas de un snapshot
 */
export function calcularEstadisticas(estadosRequisitos: SnapshotEstadoRequisito) {
  const stats = {
    total: 0,
    cumple: 0,
    noCumple: 0,
    cumpleParcialmente: 0,
    noAplica: 0,
  };

  for (const [, [estadoActual]] of Object.entries(estadosRequisitos)) {
    stats.total++;
    switch (estadoActual) {
      case 'C':
        stats.cumple++;
        break;
      case 'NC':
        stats.noCumple++;
        break;
      case 'CP':
        stats.cumpleParcialmente++;
        break;
      case 'NA':
        stats.noAplica++;
        break;
    }
  }

  const aplicables = stats.total - stats.noAplica;
  const porcentajeCumplimiento = aplicables > 0 ? (stats.cumple / aplicables) * 100 : 0;

  return {
    ...stats,
    aplicables,
    porcentajeCumplimiento: Math.round(porcentajeCumplimiento * 100) / 100,
  };
}
